import React, { useEffect, useMemo, useRef, useState } from 'react';
import StandardMCQRenderer from './StandardMCQRenderer';

export default function WorkingMemoryRenderer({ question, existingAnswer, onComplete }) {
  const sequence = useMemo(() => question?.sequenceData || null, [question?.id, question?.sequenceData]);

  if (!sequence || !Array.isArray(sequence) || !sequence.length) {
    return (
      <StandardMCQRenderer
        question={question}
        value={existingAnswer?.selectedOption || ''}
        onChange={(selectedOption) => onComplete({ selectedOption })}
        accentColor="var(--cog-primary)"
      />
    );
  }

  return <FlashRenderer question={question} sequence={sequence} existingAnswer={existingAnswer} onComplete={onComplete} />;
}

function FlashRenderer({ question, sequence, existingAnswer, onComplete }) {
  const [phase, setPhase] = useState('idle');
  const [itemIdx, setItemIdx] = useState(0);
  const [selected, setSelected] = useState(existingAnswer?.selectedOption || '');
  const [elapsedMs, setElapsedMs] = useState(existingAnswer?.responseTimeMs || 0);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const optionEntries = useMemo(() => Object.entries(question?.options || {}), [question]);

  useEffect(() => {
    if (existingAnswer) {
      setPhase('answering');
      setSelected(existingAnswer.selectedOption || '');
      setElapsedMs(existingAnswer.responseTimeMs || 0);
      return undefined;
    }

    setPhase('idle');
    setItemIdx(0);
    setSelected('');
    setElapsedMs(0);
    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(() => setPhase('showing'), 600);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [question?.id, existingAnswer, sequence.length]);

  useEffect(() => {
    if (existingAnswer) return undefined;

    if (phase === 'showing') {
      if (itemIdx < sequence.length) {
        timerRef.current = setTimeout(() => {
          setElapsedMs(Date.now() - startedAtRef.current);
          setItemIdx((i) => i + 1);
        }, 700);
      } else {
        timerRef.current = setTimeout(() => setPhase('hidden'), 300);
      }
    } else if (phase === 'hidden') {
      timerRef.current = setTimeout(() => setPhase('answering'), 1000);
    }

    return () => clearTimeout(timerRef.current);
  }, [phase, itemIdx, sequence, existingAnswer]);

  const submit = () => {
    if (!selected) return;
    onComplete({
      selectedOption: selected,
      timeTaken: Math.max(1, Math.round(elapsedMs / 1000)),
      shownSequence: sequence.join(' '),
      userAnswer: selected,
      responseTimeMs: elapsedMs
    });
  };

  return (
    <div className="assessment-shell wm-renderer">
      <div className="rounded-2xl border border-white/20 bg-slate-900/55 backdrop-blur-xl p-5 min-h-[180px] flex items-center justify-center">
        {phase === 'idle' ? <p className="text-xl text-white/70">Get ready to memorize the sequence...</p> : null}
        {phase === 'showing' && itemIdx < sequence.length ? (
          <p key={`wm_${itemIdx}`} className="wm-flash-item flash-item text-5xl font-bold tracking-widest text-violet-200">
            {sequence[itemIdx]}
          </p>
        ) : null}
        {phase === 'hidden' ? (
          <div className="wm-blank-phase blank-phase">
            <span className="text-xl text-white/50">• • •</span>
          </div>
        ) : null}
        {phase === 'answering' ? <p className="text-lg text-white/80">Which sequence did you see?</p> : null}
      </div>

      {phase === 'showing' ? (
        <div className="wm-progress-dots mt-3 flex items-center justify-center gap-2">
          {sequence.map((_, i) => (
            <span key={`wm_dot_${i}`} className={`wm-dot h-2.5 w-2.5 rounded-full ${i < itemIdx ? 'bg-violet-300' : 'bg-white/25'}`} />
          ))}
        </div>
      ) : null}

      {phase === 'answering' ? (
        <div className="wm-options mt-4 space-y-3">
          {optionEntries.map(([key, text]) => (
            <button
              key={key}
              type="button"
              onClick={() => setSelected(key)}
              className={`option-btn w-full text-left px-4 py-3 rounded-xl border transition ${selected === key ? 'selected border-violet-300 bg-gradient-to-r from-violet-600 to-purple-700 text-white' : 'border-white/25 bg-slate-800/60 text-white hover:border-violet-400/70 hover:bg-slate-700/70'}`}
            >
              <span className="font-semibold mr-2">{key}.</span>
              {text}
            </button>
          ))}

          <button
            type="button"
            onClick={submit}
            className="w-full mt-1 rounded-xl bg-gradient-to-r from-violet-600 to-purple-700 px-4 py-2.5 font-semibold"
          >
            Lock Answer
          </button>
        </div>
      ) : null}
    </div>
  );
}
